import React from 'react'
import { Tooltip } from '@surecompanies/core_components'
import { SwapOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'

import { CurrencyFormatter } from 'helpers/formatters'

import { ReconIssueRow } from '../../../api/reconciliation-v2-api-client'
import { AdjustPaymentTarget } from '../AdjustPaymentModal'
import {
  DASH,
  DISCREPANCY_ACTION_LABEL,
  DISCREPANCY_GRID_TEMPLATE,
  employeeNameOf,
  insuredNameOf,
  ISSUE_BADGE,
  ISSUE_TOOLTIP,
} from './helpers'
import { buildDiscrepancyTarget } from './row-targets'
import BenefitIdCell from './BenefitIdCell'
import CopyRowLinkButton from './CopyRowLinkButton'
import JumpToBenefitButton from './JumpToBenefitButton'
import styles from './ReviewQueue.module.scss'

const currencyFormatter = new CurrencyFormatter()

interface DiscrepancyRowProps {
  issue: ReconIssueRow
  canWrite: boolean
  onOpenTarget: (target: AdjustPaymentTarget) => void
}

/**
 * One open-discrepancy row. Cell order must match DISCREPANCY_COLUMNS in
 * helpers.ts: Type, Employee, Insured, Carrier, Benefit ID, Company, Expected,
 * Paid, Difference, Coverage period, Action.
 *
 * Expected / Paid / Difference were all on the wire and dropped before the
 * split, on the grounds that the numbers can mislead (expected may be stale and
 * over-allocation is legitimate). They are shown now, with that caveat moved
 * onto the Expected header's tooltip — spec D10 / ADR-0001.
 */
const DiscrepancyRow: React.FC<DiscrepancyRowProps> = ({
  issue,
  canWrite,
  onOpenTarget,
}) => {
  const badge = ISSUE_BADGE[issue.issueType]

  return (
    <div
      className={styles.row}
      style={{ gridTemplateColumns: DISCREPANCY_GRID_TEMPLATE }}
    >
      <span className={styles.cell}>
        <Tooltip title={ISSUE_TOOLTIP[issue.issueType]}>
          <span className={`${styles.badge} ${styles[badge.tone]}`}>
            {badge.label}
          </span>
        </Tooltip>
      </span>

      {/* Two cells, not one "Employee / Insured": either may be empty now that
          neither falls back to the other. */}
      <span className={styles.cell}>
        <span className={styles.fullText}>{employeeNameOf(issue) ?? DASH}</span>
      </span>

      <span className={styles.cell}>
        <span className={styles.fullText}>{insuredNameOf(issue) ?? DASH}</span>
      </span>

      <span className={styles.cell}>
        <span className={styles.truncate}>{issue.carrierName ?? DASH}</span>
      </span>

      <BenefitIdCell benefitId={issue.benefitId} />

      <span className={styles.cell}>
        <span className={styles.truncate}>{issue.companyName ?? DASH}</span>
      </span>

      <span className={`${styles.cell} ${styles.num}`}>
        {currencyFormatter.safeFormat(issue.expected, DASH)}
      </span>

      <span className={`${styles.cell} ${styles.num}`}>
        {currencyFormatter.safeFormat(issue.paid, DASH)}
      </span>

      {/* Signed paid − expected, straight off the wire — never recomputed here,
          so a null Expected can't ship a NaN. */}
      <span className={`${styles.cell} ${styles.num}`}>
        {currencyFormatter.safeFormat(issue.discrepancy, DASH)}
      </span>

      {/* Discrepancy rows carry no transaction timestamp — only the affected
          coverage month. */}
      <span className={`${styles.cell} ${styles.dateCell}`}>
        {dayjs(issue.periodStartDate).format('MMM YYYY')}
      </span>

      <span className={`${styles.cell} ${styles.actionCell}`}>
        <button
          className={styles.actionButton}
          type="button"
          disabled={!canWrite}
          // Built by the same helper an `rqOpen` deep link goes through, so a
          // row opened from a shared link and one opened from this button
          // cannot drift.
          onClick={() => onOpenTarget(buildDiscrepancyTarget(issue))}
        >
          <SwapOutlined aria-hidden />
          {DISCREPANCY_ACTION_LABEL}
        </button>
        {/* Unconditional here — ReconIssueRow.benefitId is not nullable. */}
        <JumpToBenefitButton
          benefitId={issue.benefitId}
          date={issue.periodStartDate}
        />
        <CopyRowLinkButton
          target={{
            kind: 'discrepancy',
            benefitId: issue.benefitId,
            periodStartDate: issue.periodStartDate,
            issueType: issue.issueType,
          }}
        />
      </span>
    </div>
  )
}

export default DiscrepancyRow
